import React, { useState, useEffect } from "react";
import { FaTrash, FaTimes, FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import "../styles/common.css";
import "./DeleteConfirmModal.css";

/**
 * Composant réutilisable pour les modals de confirmation de suppression
 * @param {boolean} isOpen - Si le modal est ouvert
 * @param {function} onClose - Fonction appelée pour fermer le modal
 * @param {function} onConfirm - Fonction appelée lors de la confirmation
 * @param {string} itemName - Nom de l'élément à supprimer (pour la confirmation ou l'affichage)
 * @param {string} itemType - Type d'élément (ex: "animal", "événement")
 * @param {Array} consequences - Liste des conséquences de la suppression
 * @param {boolean} requireConfirmation - Si true, affiche le champ de confirmation par saisie (défaut: true)
 * @param {boolean} isLoading - Si true, affiche un état de chargement (défaut: false)
 */
export default function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  itemName = "",
  itemType = "élément",
  consequences = [],
  requireConfirmation = true,
  isLoading = false,
}) {
  const [confirmText, setConfirmText] = useState("");

  // Réinitialiser le champ de confirmation quand le modal se ferme
  useEffect(() => {
    if (!isOpen) {
      setConfirmText("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    // Si la confirmation par saisie est requise, valider
    if (requireConfirmation) {
      if (!itemName) {
        toast.error(`Le ${itemType} est requis pour la confirmation.`);
        return;
      }
      if (confirmText.trim().toUpperCase() !== itemName.trim().toUpperCase()) {
        toast.error(`Le ${itemType} saisi ne correspond pas. Veuillez réessayer.`);
        return;
      }
    }
    onConfirm();
  };

  const handleClose = () => {
    if (isLoading) return; // Empêcher la fermeture pendant le chargement
    setConfirmText("");
    onClose();
  };

  const isConfirmDisabled = requireConfirmation
    ? confirmText.trim().toUpperCase() !== (itemName || "").trim().toUpperCase()
    : false;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header modal-header-danger">
          <FaTrash className="modal-icon modal-icon-danger" />
          <h3>Confirmer la suppression</h3>
        </div>
        <div className="modal-body">
          <div className="delete-warning">
            <p className="warning-title">⚠️ Attention : Cette action est irréversible</p>
            {!requireConfirmation && (
              <p className="warning-text">
                Êtes-vous sûr de vouloir supprimer {itemName ? (
                  <>
                    {itemType === "troupeau" ? "le" : itemType === "berger" ? "le" : "cet"} <strong>{itemName}</strong> ?
                  </>
                ) : (
                  <>
                    cette {itemType} ? Cette action est irréversible.
                  </>
                )}
              </p>
            )}
            {consequences.length > 0 && (
              <>
                {requireConfirmation && (
                  <p className="warning-text">
                    La suppression de ce {itemType} entraînera :
                  </p>
                )}
                <ul className="warning-list">
                  {consequences.map((consequence, index) => (
                    <li key={index}>{consequence}</li>
                  ))}
                </ul>
              </>
            )}
            {consequences.length === 0 && (
              <>
                {requireConfirmation && (
                  <p className="warning-text">
                    La suppression de ce {itemType} entraînera :
                  </p>
                )}
                <ul className="warning-list">
                  <li>La perte définitive de toutes les données</li>
                  <li>La suppression de tous les éléments associés</li>
                </ul>
              </>
            )}
          </div>
          {requireConfirmation && itemName && (
            <div className="delete-confirmation">
              <label>
                Pour confirmer, veuillez saisir le {itemType} : <strong>{itemName}</strong>
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={`Saisir le ${itemType}`}
                className="modern-input delete-matricule-input"
                autoFocus
                disabled={isLoading}
              />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            onClick={handleClose}
            className="modern-button modern-button-secondary"
            disabled={isLoading}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="modern-button modern-button-danger"
            disabled={isConfirmDisabled || isLoading}
          >
            {isLoading ? (
              <>
                <FaSpinner className="spinning" />
                <span>Suppression...</span>
              </>
            ) : (
              <>
                <FaTrash />
                <span>Confirmer la suppression</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

